/* eslint-disable no-unreachable */
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  concat,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  Authenticator,
  Heading,
  Image,
  Text,
  useTheme,
  View,
  withAuthenticator,
} from "@aws-amplify/ui-react";
import { onError } from "apollo-link-error";
import { Auth } from "aws-amplify";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import QRCode from "qrcode"; // Import QRCode library
import DialogHoc from "../components/hoc/Dialog/dialogHOC";
import DrawerHoc from "../components/hoc/Drawer/drawerHOC";
import Layout from "../components/layout";
import { isPlatformAdmin } from "../hooks/utils";
import { COPY_RIGHTS, GRAPHQL_URL, SMAI_LOGO } from "../config";
import { GET_REFRESH_TOKEN } from "../graphql/mutation";
import {
  isLogoutInProgress,
  registerLogoutOverlaySetter,
  signalLogoutEnded,
  signalLogoutStarted,
} from "../library/logoutOverlay";
import { runPreLogout } from "../library/preLogout";
import resolvers from "../graphql/resolvers";
import typeDefs from "../graphql/typeDefs";
import { colorConfig } from "../library/colorConfig";
import { getLocationId } from "../redux/slices/locationSlice";
import {
  ERROR_BOUNDARY_REDIRECT_PATH,
  getLocalStorage,
  removeLocalStorage,
} from "../utils/storages";

const utc = require("dayjs/plugin/utc");
// import utc from 'dayjs/plugin/utc' // ES 2015

const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = dayjs.tz.guess(); // America/Chicago

// Browser-controlled signatures used to detect transient/network failures
// when refreshing the session. We match against these (rather than AWS's
// auth-error wording) so the sign-out logic stays correct even if Amplify
// changes its error messages.
const TRANSIENT_ERROR_SIGNATURES = ["network", "timeout", "failed to fetch"];
const NETWORK_ERROR_CODE = "NetworkError";

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  if (networkError) {
    console.log(`[NetworkError error]: Message: ${networkError.message}`);
  }
});

// Module-scoped flag indicating a sign-out is in progress. Kept here (not on
// `window` or sessionStorage) so it's SSR-safe and survives the
// sessionStorage.clear() that runs during logout teardown. Token refresh bails
// while this is true to avoid "user is not authenticated" errors and redundant
// sign-outs from refresh calls that race with logout. Reset on fresh login.
let isSigningOut = false;
export const setIsSigningOut = value => {
  isSigningOut = value;
};
// Read-only accessor for screens that must not fire user-scoped queries while a
// logout is tearing redux down (e.g. the activity center's lead list, whose
// filters are reset during logout and would otherwise trigger one last
// getLeads against a session that is already going away).
export const getIsSigningOut = () => isSigningOut;

const getJwtToken = async () => {
  return new Promise(resolve => {
    const storedToken = sessionStorage.getItem("token");
    const exp = sessionStorage.getItem("exp");
    let isExpired = false;
    if (exp) {
      const expiredDate = new Date(parseInt(exp));
      isExpired = expiredDate <= new Date();
    }
    if (!storedToken || !exp || isExpired) {
      Auth.currentSession()
        .then(session => session.getAccessToken())
        .then(accessToken => {
          if (accessToken && accessToken !== null) {
            const jwtToken = accessToken.getJwtToken();
            sessionStorage.setItem("token", jwtToken);
            sessionStorage.setItem("exp", (accessToken.payload.exp * 1000).toString());
            resolve(jwtToken);
          }
        });
    } else {
      resolve(storedToken);
    }
  });
};

function deleteAllCookies() {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

const refreshToken = () => {
  return new Promise((resolve, reject) => {
    (async () => {
      // Don't attempt a refresh while a sign-out is in progress — the session
      // is being torn down, so a refresh would fail with "user is not
      // authenticated" and could trigger a redundant sign-out.
      if (isSigningOut) {
        resolve(null);
        return;
      }
      const storedToken = sessionStorage.getItem("token");
      const exp = sessionStorage.getItem("exp");
      let almostExpired = false;
      if (exp) {
        const expiredDate = new Date(parseInt(exp) - 15 * 60 * 1000);
        almostExpired = expiredDate <= new Date();
      }
      if (!storedToken || !exp || almostExpired) {
        try {
          const cognitoUser = await Auth.currentAuthenticatedUser();
          const currentSession = await Auth.currentSession();
          if (!currentSession || !currentSession.refreshToken) {
            console.log("No valid session found");
            // Token-recovery sign-outs skip the BE pre-logout mutation
            // (the token's already invalid), but the UI should still
            // show the overlay so the page doesn't sit in empty-state
            // limbo while Cognito + redirect resolve.
            signalLogoutStarted();
            sessionStorage.clear();
            localStorage.clear();
            deleteAllCookies();
            if (process.env.GATSBY_POSTHOG_API_KEY) {
              window?.posthog?.reset();
            }
            Auth.signOut().finally(() => {
              window.location.href = "/";
            });
            reject(new Error("No valid session"));
            return;
          }
          cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {
            if (err || !session) {
              console.log("Unable to refresh session", err);
              signalLogoutStarted();
              sessionStorage.clear();
              localStorage.clear();
              deleteAllCookies();
              if (process.env.GATSBY_POSTHOG_API_KEY) {
                window?.posthog?.reset();
              }
              Auth.signOut().finally(() => {
                window.location.href = "/";
              });
              reject(err);
              return;
            }
            const { accessToken } = session;
            const jwtToken = accessToken.getJwtToken();
            sessionStorage.setItem("token", jwtToken);
            sessionStorage.setItem("exp", (accessToken.payload.exp * 1000).toString());
            resolve(jwtToken);
          });
        } catch (e) {
          // A refresh that was already in-flight when sign-out started will
          // reject here as the session is torn down. Re-check the flag and
          // exit quietly — no error log, no redundant sign-out.
          if (isSigningOut) {
            resolve(null);
            return;
          }
          console.log("Unable to refresh token", e);
          // Only force sign-out on genuine auth failures (refresh token
          // expired/revoked or no session). Transient errors such as a
          // network blip can also reject here even though the refresh token
          // is still valid — in that case keep the user logged in and let
          // the next interval retry, instead of unfairly signing them out.
          // Don't rely on AWS's exact auth-error wording (it can change).
          // Instead, only SKIP sign-out for transient/network errors — these
          // have stable, browser-controlled signatures. Any other failure to
          // obtain a session is treated as "session is gone" → sign out.
          const errMessage = (typeof e === "string" ? e : e?.message || "").toLowerCase();
          const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
          const isTransientError =
            isOffline ||
            TRANSIENT_ERROR_SIGNATURES.some(sig => errMessage.includes(sig)) ||
            e?.code === NETWORK_ERROR_CODE;

          if (!isTransientError) {
            sessionStorage.clear();
            localStorage.clear();
            deleteAllCookies();
            if (process.env.GATSBY_POSTHOG_API_KEY) {
              window?.posthog?.reset();
            }
            Auth.signOut().finally(() => {
              window.location.href = "/";
            });
          }
          reject(e);
        }
      } else {
        resolve(storedToken);
      }
    })();
  });
};

export const UserContext = React.createContext();

/**
 * Full-screen overlay shown while a logout flow is in progress.
 *
 * Mounted by ``AuthenticatorProvider`` when ``isLoggingOut`` flips on
 * (either via React state or the module-scoped pub-sub in
 * ``library/logoutOverlay.js``). The provider gates rendering on the
 * flag; this component only owns the visual.
 *
 * Self-contained — no antd dependency, inline styles + a tiny @keyframes
 * for the spinner — so we don't drag a library into the auth bootstrap.
 */
const LogoutOverlay = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Signing out"
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 999999,
      background: "rgba(255, 255, 255, 0.92)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      // Catch any pointer events so the page underneath can't be
      // interacted with while the cleanup + sign-out resolves.
      pointerEvents: "all",
    }}
  >
    <style>{`
      @keyframes engagedai-logout-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
    <div
      style={{
        width: 48,
        height: 48,
        border: "4px solid rgba(0, 0, 0, 0.08)",
        borderTopColor: "#1677ff",
        borderRadius: "50%",
        animation: "engagedai-logout-spin 0.9s linear infinite",
      }}
    />
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "rgba(0, 0, 0, 0.65)",
        letterSpacing: 0.2,
      }}
    >
      Signing out…
    </div>
  </div>
);

const AuthenticatorProvider = props => {
  const { persistor, children, authData, authState } = props;

  const locationIdslice = useSelector(state => getLocationId(state));

  const [client, setClient] = useState(null);
  const [token, setToken] = useState(null);
  const [auth, setAuth] = useState(null);
  const [modal, setmodal] = useState(false);
  const [fullScreen, setMaximizeScreen] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [loadingAuth, setloadingAuth] = useState(false);
  const [addnewLead, setAddNewLead] = useState(false);
  const [newLead, setNewLead] = useState(null);
  const [socketMsgSelectedLead, setSocketMsgSelectedLead] = useState("");
  const [socket, setSocket] = useState(null);
  const [refetchAppointments, setrefetchAppointments] = useState(false);
  const [isUserIsBeingSet, setIsUserIsBeingSet] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(true);
  // Set to true the moment any logout flow begins (FE button click,
  // disabled-account redirect, error boundary), regardless of which
  // path runs the BE cleanup / Cognito sign-out. Drives the full-screen
  // overlay below so the user doesn't see an empty-data layout while
  // the mutation + sign-out resolve.
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [user, setUser] = useState({
    email: "",
    fullName: "",
    phone: "",
    isDisabled: false,
    locationId: "",
    company: {
      name: "",
      isDisabled: false,
      aingineSourceId: null,
      isOptinConsentMethod: false,
      crmIntegrationType: "",
    },
    role: {
      name: "default",
      canCreateTeams: false,
      canCreateUsers: false,
      canViewProspects: false,
    },
    teamsLeader: null,
    accessControlPermissions: [],
  });

  const [mfaStep, setMfaStep] = useState(false);
  const [isFirstTimeMFA, setIsFirstTimeMFA] = useState(false);
  const [cognitoAuthUser, setCognitoAuthUser] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null); // State to store QR code image
  const [showQrCode, setShowQrCode] = useState(false); // State to control QR code visibility
  const [verificationCode, setVerificationCode] = useState(""); // State for user input

  const handleFirstTimeMFASetup = async cognitoUser => {
    try {
      const mfaEnabled = cognitoUser.preferredMFA || "NOMFA"; // Use preferredMFA from cognitoUser
      console.log("handleFirstTimeMFASetup >>>>>> Preferred MFA:", mfaEnabled);

      if (mfaEnabled === "SOFTWARE_TOKEN_MFA") {
        setAuth(cognitoUser);
        setloadingAuth(true);
      } else if (mfaEnabled === "NOMFA") {
        const totpCode = await Auth.setupTOTP(cognitoUser); // Generate TOTP secret
        // const totpCode = "KKYTKK7ZJUPONVPIXM2DCXESLBA4GIAJBIUB7SJNSLWQF6I4VEKQ";

        // Generate QR code URL
        const qrCodeUrl = `otpauth://totp/${
          cognitoUser?.attributes?.email || cognitoUser.username
        }?secret=${totpCode}&issuer=EngagedAi`;
        const qrCodeImage = await QRCode.toDataURL(qrCodeUrl);
        setQrCodeImage(qrCodeImage);
        setShowQrCode(true); // Show the QR code
      } else {
        console.warn("Unexpected MFA type:", mfaEnabled);
        setIsFirstTimeMFA(true);
      }
    } catch (error) {
      console.error("Error during MFA setup:", error);
      if (error?.message?.includes("User has not verified software token mfa")) {
        setShowQrCode(true); // Show QR code if verification is pending
      }
    }
  };

  const handleVerifyCode = async cognitoUser => {
    try {
      await Auth.verifyTotpToken(cognitoUser, verificationCode); // Verify the TOTP token
      await Auth.setPreferredMFA(cognitoUser, "SOFTWARE_TOKEN_MFA"); // Enable software token MFA
      setIsFirstTimeMFA(true);
      setShowQrCode(false); // Hide QR code after verification
      setAuth(cognitoUser);
      setloadingAuth(true);
    } catch (error) {
      console.error("Error verifying TOTP token:", error);
      if (error?.code === "EnableSoftwareTokenMFAException") {
        try {
          await Auth.setPreferredMFA(cognitoUser, "SOFTWARE_TOKEN_MFA"); // Explicitly enable MFA
          setIsFirstTimeMFA(true);
          setShowQrCode(false); // Hide QR code after enabling MFA
        } catch (enableError) {
          console.error("Error enabling software token MFA:", enableError);
          alert("Failed to enable MFA. Please try again.");
        }
      } else {
        alert("Invalid verification code. Please try again.");
      }
    }
  };

  // Register the overlay setter with the module-scoped pub-sub so
  // non-React logout paths (errorBoundry, layoutHelpers, the
  // runPreLogout helper) can flip the overlay without going through
  // React context. Re-registers on every mount; safe because the
  // store keeps only the latest setter.
  useEffect(() => {
    registerLogoutOverlaySetter(setIsLoggingOut);
    return () => registerLogoutOverlaySetter(null);
  }, []);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async cognitoUser => {
        const mfaVerified = sessionStorage.getItem("mfaCodeVerified");
        const isAdminUser = isPlatformAdmin(cognitoUser);

        const cognitoUserMFASetting = cognitoUser?.attributes?.locale
          ? JSON.parse(cognitoUser?.attributes?.locale || "")
          : {};
        if (
          !mfaVerified &&
          cognitoUser.preferredMFA === "NOMFA" &&
          !isAdminUser &&
          cognitoUserMFASetting?.mfa_enabled
        ) {
          handleFirstTimeMFASetup(cognitoUser);
          setCognitoAuthUser(cognitoUser);
        } else {
          setAuth(cognitoUser);
          setloadingAuth(true);
        }
        // Check for redirect path after successful authentication
        const redirectPath = getLocalStorage(ERROR_BOUNDARY_REDIRECT_PATH);
        if (redirectPath) {
          window.location.href = redirectPath;
          removeLocalStorage(ERROR_BOUNDARY_REDIRECT_PATH);
        }
      })
      .catch(error => {
        console.error("Error fetching authenticated user:", error);
      });
  }, []);

  const updateUser = _loginUser => {
    let loginUser = { ..._loginUser };
    loginUser.token = token;

    const allActiveUserAccounts = loginUser?.userAccounts?.filter(res => {
      if (!res.company.isDisabled && !res.isDisabled) {
        return true;
      }
    });

    const allUserAccounts = allActiveUserAccounts.map(res => {
      return res.company;
    });

    let userRoleObj = loginUser.role;
    if (loginUser?.company?.userRoles?.length > 0) {
      const found = loginUser.company.userRoles.find(x => x.userId === Number(loginUser.id));
      userRoleObj = found ? found.role : userRoleObj;
    }

    const getActiveCrmIntegration = company => {
      let type = "";
      let { locationId } = loginUser;
      if (sessionStorage?.getItem("currentLocationId")?.includes(",")) {
        //
      } else {
        locationId = sessionStorage.getItem("currentLocationId");
      }
      if ((company?.crmIntegration || []).length > 0) {
        const findIndex = (company?.crmIntegration).findIndex(
          el => el.active && +el.locationId === +locationId
        );
        if (findIndex > -1) {
          type = company?.crmIntegration[findIndex].integrationType;
        }
      }
      return type;
    };

    const userTemp = {
      ...loginUser,
      id: loginUser.id,
      email: loginUser.email,
      fullName: loginUser.fullName,
      phone: loginUser.phone,
      firstName: loginUser.firstName,
      lastName: loginUser.lastName,
      isDisabled: loginUser.isDisabled,
      locationId: loginUser.locationId,
      company: {
        id: loginUser.company.id,
        name: loginUser.company.name,
        isDisabled: loginUser.company.isDisabled,
        aingineSourceId: loginUser.company.aingineSourceId,
        timezone: loginUser.company.timezone,
        isOptinConsentMethod: loginUser.company.isOptinConsentMethod,
        crmIntegrationType: getActiveCrmIntegration(loginUser?.company),
        isOpenCrmWarningPopup: loginUser.company.isOpenCrmWarningPopup,
      },
      role: userRoleObj,
      teamsLeader: loginUser.teamsLeader,
      userAccounts: allUserAccounts.sort((a, b) => (a.name > b.name ? 1 : -1)),
      accessControlPermissions: user.accessControlPermissions,
      companyRole: loginUser?.companyRole || {},
      location: loginUser?.location,
      locations: loginUser?.locations,
    };
    loginUser = userTemp;
    setIsUserIsBeingSet(false);
    setUser(loginUser);
  };

  const updateUserWithPermissions = (_loginUser, permissions) => {
    let loginUser = { ..._loginUser };
    loginUser.token = token;

    const allActiveUserAccounts = (loginUser.userAccounts || []).filter(res => {
      if (!res.company.isDisabled && !res.isDisabled) {
        return true;
      }
    });

    const allUserAccounts = allActiveUserAccounts.map(res => {
      return res.company;
    });

    let userRoleObj = loginUser.role;
    if (loginUser?.company?.userRoles?.length > 0) {
      const found = loginUser.company.userRoles.find(x => x.userId === Number(loginUser.id));
      userRoleObj = found ? found.role : userRoleObj;
    }

    const getActiveCrmIntegration = company => {
      let type = "";
      let { locationId } = loginUser;
      if (sessionStorage?.getItem("currentLocationId")?.includes(",")) {
        //
      } else {
        locationId = sessionStorage.getItem("currentLocationId");
      }
      if ((company?.crmIntegration || []).length > 0) {
        const findIndex = (company?.crmIntegration).findIndex(
          el => el.active && +el.locationId === +locationId
        );
        if (findIndex > -1) {
          type = company?.crmIntegration[findIndex].integrationType;
        }
      }
      return type;
    };

    const userTemp = {
      ...loginUser,
      id: loginUser.id,
      email: loginUser.email,
      fullName: loginUser.fullName,
      phone: loginUser.phone,
      firstName: loginUser.firstName,
      lastName: loginUser.lastName,
      isDisabled: loginUser.isDisabled,
      locationId: loginUser.locationId,
      company:
        {
          ...loginUser?.company,
          crmIntegrationType: getActiveCrmIntegration(loginUser?.company),
        } || {},
      role: userRoleObj,
      teamsLeader: loginUser.teamsLeader,
      userAccounts: allUserAccounts.sort((a, b) => (a.name > b.name ? 1 : -1)),
      accessControlPermissions: permissions,
      companyRole: loginUser?.companyRole || {},
      location: loginUser?.location,
      locations: loginUser?.locations,
    };
    loginUser = userTemp;
    setIsUserIsBeingSet(false);
    setUser(loginUser);
  };

  const httpLink = new HttpLink({ uri: GRAPHQL_URL });

  // Logout-gate link REMOVED. The synthetic empty-data responses were
  // causing two cascading problems on the new Apollo 3.14 client:
  //   1. ``onCompleted`` handlers fire on every gated query and many
  //      call setState unconditionally; with null/undefined data the
  //      derived state thrashes the render loop and the logout
  //      overlay hangs forever ("continues loading").
  //   2. Apollo 3.14 flags every onCompleted handler that sets local
  //      state with InvariantError #13 — a wall of noisy warnings.
  //
  // Prod-blue's behavior (no gate) is the right baseline: queries
  // during the logout window go to the BE, get "Unauthorized"
  // responses, the FE shows a brief toast or two, and Auth.signOut
  // + redirect complete normally. The flash of toasts is acceptable;
  // a hung logout overlay is not.

  // Preemptive auth: before every request, ensure the JWT isn't expired.
  // If the stored `exp` is in the past (or within a 30s clock-skew buffer),
  // await refreshToken() and use the fresh JWT. This prevents the
  // "Signature has expired" failures that happened after the tab was idle
  // long enough for setInterval-based refresh to be throttled by the browser.
  const authMiddleware = setContext(async (_, { headers = {} }) => {
    const exp = sessionStorage.getItem("exp");
    const isExpired = exp ? new Date(parseInt(exp) - 30 * 1000) <= new Date() : true;

    let tokenInfo;
    if (isExpired) {
      try {
        tokenInfo = await refreshToken();
      } catch {
        // refreshToken handles sign-out itself on hard failure;
        // fall back to whatever's in storage so this request fails cleanly
        tokenInfo = sessionStorage.getItem("token") || token;
      }
    } else {
      tokenInfo = sessionStorage.getItem("token") || token;
    }

    let customHeader = {
      ...headers,
      authorization: tokenInfo ? `Bearer ${tokenInfo}` : "",
      refreshToken: sessionStorage.getItem("refreshToken"),
      // Identifies the calling client surface (WEB / EXTENSION / IOS /
      // ANDROID / IPAD). The webapp gates the SMS-via-lambda cutover on
      // this header — strict-parsing mobile clients break on the new
      // `lead_message=null` response shape, so the cutover only fires
      // for platforms the backend SSM allowlist permits. The extension
      // renders inside the same SPA at /browser-ext* URLs and shares
      // this Apollo client; detect it by URL so ops can independently
      // gate the cutover for the embedded-iframe surface via the SSM
      // ``MUTATION_SMS_VIA_LAMBDA_PLATFORMS`` allowlist.
      "X-Client-Platform":
        typeof window !== "undefined" && window.location?.pathname?.includes("browser-ext")
          ? "EXTENSION"
          : "WEB",
    };
    if (locationIdslice != null || sessionStorage.getItem("currentLocationId") !== null) {
      // Prefer sessionStorage.currentLocationId — it represents the live
      // session location and is updated by every legitimate location-change
      // path (dropdown, location-match useEffect, multi-location dispatch).
      // locationIdslice (Redux) can hold a stale value across in-tab SPA
      // navigations: when the user moves from a URL with crmDealerId=X
      // (matched to location A) to a URL without crmDealerId, the
      // location-match useEffect bails and Redux retains A — causing
      // sLocationId to scope subsequent requests to A even though the
      // session now points to a different location. Fall back to
      // locationIdslice only when sessionStorage hasn't been populated yet
      // (initial-mount race before any handler runs). "all" is a UI marker
      // that lives in Redux while sessionStorage holds the comma-separated
      // location ids — sessionStorage is the right source there too.
      const sessionLocationId = sessionStorage.getItem("currentLocationId");
      customHeader = {
        ...customHeader,
        sLocationId:
          sessionLocationId ||
          (locationIdslice && locationIdslice !== "all" ? locationIdslice : null),
        "X-Timezone": timeZone,
      };
    }
    // EAI-1486: admin impersonation. When an admin has started impersonating
    // a user, carry the target user id on every request. The backend swaps
    // request.user to this target (dropping the admin bypass) only when the
    // caller's real JWT is a platform admin, so a non-admin setting this key
    // is a harmless no-op server-side. Stored in sessionStorage so it
    // survives in-tab SPA navigations but clears on tab close.
    const impersonateUserId = sessionStorage.getItem("impersonateUserId");
    if (impersonateUserId) {
      customHeader = {
        ...customHeader,
        "X-Impersonate-User": impersonateUserId,
      };
    }
    return { headers: customHeader };
  });

  const getRefreshToken = async () => {
    const currentSession = await Auth.currentSession();
    sessionStorage.setItem("refreshToken", currentSession.getRefreshToken().token);
  };

  useEffect(() => {
    // Clear any stale sign-out flag from a previous session so token refresh
    // works again after logging back in (navigate("/") is client-side and
    // doesn't reload the page, so the flag would otherwise persist).
    setIsSigningOut(false);
    // Also reset the module-scoped ``loggingOut`` flag that gates the
    // Apollo link. Without this, a previous logout's ``signalLogoutStarted()``
    // stays sticky across the in-tab navigate("/") → fresh login cycle
    // and every subsequent query gets short-circuited with ``{data: {}}``.
    // That cascade looks exactly like an "everything is broken on
    // login" symptom: ``Cannot read properties of undefined (reading
    // 'map')``, ``undefined is not iterable``, Apollo InvariantError
    // #13 on cache writes, etc.
    signalLogoutEnded();
    sessionStorage.setItem("isLogin", true);
    getRefreshToken();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("isLogin")) {
      getJwtToken().then(t => {
        setToken(t);
      });
      const links = ApolloLink.from([errorLink, httpLink]);
      const apolloClient = new ApolloClient({
        cache: new InMemoryCache({
          addTypename: false,
        }),
        link: concat(authMiddleware, links),
        resolvers,
        typeDefs,
      });
      // Expose on window so non-React logout paths (errorBoundry,
      // legacy helpers) can fire the pre-logout BE mutation without
      // needing to thread the client through every call site.
      if (typeof window !== "undefined") {
        // eslint-disable-next-line no-underscore-dangle
        window.__APOLLO_CLIENT__ = apolloClient;
      }
      setClient(apolloClient);

      const refreshInterval = setInterval(() => {
        // Skip the refresh entirely while a logout flow is in
        // progress — Cognito has no current user, so currentSession
        // and currentAuthenticatedUser reject with "No current user"
        // and the unhandled promise rejection surfaces in the console.
        if (isLogoutInProgress()) return;
        refreshToken()
          .then(newToken => {
            if (!newToken) return;
            return Auth.currentSession()
              .then(() => Auth.currentAuthenticatedUser())
              .then(() => {});
          })
          .catch(() => {
            // refreshToken / Auth.* reject after logout (or when the
            // Cognito session has been invalidated server-side).
            // Swallow — refreshToken's own error path handles the
            // genuine sign-out cascade for non-transient failures.
          });
      }, 5 * 60 * 1000);

      return () => clearInterval(refreshInterval);
    }
  }, [token, locationIdslice]);

  if (showQrCode) {
    const { tokens } = useTheme();
    return (
      <div
        className="min-h-screen qrcode_page"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <View textAlign="center" padding={tokens.space.large}>
          <Image alt="smai logo" src={SMAI_LOGO} className="qrcode_logo w-[80%]" />
        </View>
        <View textAlign="center" padding="medium" className="qrcode_box w-[25%]">
          <Heading
            color={colorConfig.WHITE}
            padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
            level={3}
          >
            Scan the QR Code
          </Heading>
          <Text
            color={colorConfig.WHITE}
            padding={`0 ${tokens.space.xl} ${tokens.space.small} ${tokens.space.xl}`}
            style={{ fontSize: "14px" }}
          >
            Use your authenticator app to scan the QR code below or manually enter the secret.
          </Text>
          {qrCodeImage && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Image
                src={qrCodeImage}
                alt="QR Code"
                style={{
                  margin: "20px 0",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  padding: "10px",
                  backgroundColor: "#fff",
                }}
              />
              <a
                href={qrCodeImage}
                download="enagedai-qr-code.png"
                style={{
                  color: "#047d95",
                  textDecoration: "none",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Download QR Code
              </a>
            </div>
          )}
          <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`}>
            <input
              type="text"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              placeholder="Enter verification code"
              style={{
                width: "100%",
                padding: "10px",
                marginTop: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={() => handleVerifyCode(cognitoAuthUser)}
              className="amplify-button--primary w-full p-3 mt-4 rounded-md"
            >
              Verify
            </button>
          </View>
          <View textAlign="center" padding={tokens.space.medium}>
            <a
              href="/"
              style={{
                color: "#047d95",
                textDecoration: "none",
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={() => {
                // Run BE pre-logout cleanup (user-scoped cache busts)
                // before the FE-only Cognito sign-out. Best-effort.
                runPreLogout().finally(() => {
                  Auth.signOut().then(() => {
                    setQrCodeImage(null);
                    setShowQrCode(false);
                    sessionStorage.clear();
                    localStorage.clear();
                    deleteAllCookies();
                    if (process.env.GATSBY_POSTHOG_API_KEY) {
                      window?.posthog?.reset();
                    }
                  });
                });
              }}
            >
              Back to Sign In
            </a>
          </View>
        </View>
        <View textAlign="center" padding={tokens.space.large} className="w-[25%]">
          <Text
            color={{
              base: colorConfig.WHITE,
            }}
          >
            {COPY_RIGHTS}
          </Text>
        </View>
      </div>
    );
  }

  const authContent = auth || authData;

  return client && loadingAuth ? (
    <ApolloProvider client={client}>
      <UserContext.Provider
        value={{
          user: user,
          fullScreen,
          setMaximizeScreen,
          notificationData,
          socket,
          setSocket,
          setNotificationData,
          setUser,
          authData: authContent,
          socketMsgSelectedLead,
          setSocketMsgSelectedLead,
          modal: modal,
          setmodal: setmodal,
          isUserIsBeingSet,
          persistor: persistor,
          newLead,
          setNewLead,
          addnewLead,
          setAddNewLead,
          isFullScreen,
          setIsFullScreen,
          refetchAppointments,
          setrefetchAppointments,
          isLoggingOut,
        }}
      >
        {isLoggingOut && <LogoutOverlay />}
        <DialogHoc />
        <DrawerHoc />
        <Layout
          authData={authContent}
          updateUser={updateUser}
          updateUserWithPermissions={updateUserWithPermissions}
        >
          {React.cloneElement(children, {
            authState: authState,
            authData: authContent,
          })}
        </Layout>
      </UserContext.Provider>
    </ApolloProvider>
  ) : null;
};

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image alt="smai logo" src={SMAI_LOGO} />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text
          color={{
            base: colorConfig.WHITE,
          }}
        >
          {COPY_RIGHTS}
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          color={colorConfig.WHITE}
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Sign in to your account
        </Heading>
      );
    },
    Footer() {
      const { tokens } = useTheme();
      return (
        <>
          <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`} textAlign="center">
            <span style={{ color: "#fff", fontSize: 14 }}>
              By clicking sign-in, I agree to be subject to the{" "}
              <a
                href="https://engagedai.io/termsofservice"
                target="_blank"
                style={{
                  textDecoration: `none`,
                  color: "#047d95",
                }}
                rel="noreferrer"
              >
                Terms of Service
              </a>
              .
            </span>
          </View>
          <View textAlign="center" padding={`${tokens.space.small} 0 ${tokens.space.small} 0`}>
            <Authenticator.SignIn.Footer />
          </View>
        </>
      );
    },
  },

  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          color={colorConfig.WHITE}
          // padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={4}
          className="text-left"
        >
          Enter the verification code from your authenticator app
        </Heading>
      );
    },
    Footer() {
      const { tokens } = useTheme();

      return (
        <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`} textAlign="center">
          <span style={{ color: "#fff", fontSize: 14 }}>
            By clicking sign-in, I agree to be subject to the{" "}
            <a
              href="https://engagedai.io/termsofservice"
              target="_blank"
              style={{
                textDecoration: `none`,
                color: "#047d95",
              }}
              rel="noreferrer"
            >
              Terms of Service
            </a>
            .
          </span>
        </View>
      );
    },
    // async onSuccess() {
    //   // Redirect to engagements/activity-center/ after successful sign-in
    //   window.location.href = "/engagements/activity-center/";
    // },
  },
};

export default withAuthenticator(AuthenticatorProvider, {
  components,
  hideSignUp: true,
});
